/* eslint-disable no-comments/disallowComments */
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  ActivityReportsDto,
  DailyInfo,
  DriverDto,
  DriverLogbookRecord,
  DriverStatusSummaryDto,
  DriverWeightStationLogs,
  EventLogCoDriverPeriod,
  FmcsaReportsDto,
  FmcsaValidationDto,
  GroupedByDriversByDateDto,
  LogbookReportParams,
  LogRecord,
} from '@fleet-beat/api';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  DataSourceParams,
  Environment,
  GetManyV2Dto,
  GetOneV2Dto,
  PageableListV2Dto,
  ResponseDto,
  SimpleResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { TransferOutputFileDto } from '../../models/fmcsa-reports/transfer-output-file.dto';

@Injectable({
  providedIn: 'root',
})
export class DriversApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/drivers`;
  private readonly baseUrlSummary: string = `${this.environment.apiEndpoint}/companies`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<DriverDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<DriverDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  patchOne(id: string, data: DriverDto, auth: AuthHeaders): Observable<DriverDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  postOne(data: DriverDto, auth: AuthHeaders): Observable<DriverDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyByIds(ids: string[], include: string, auth: AuthHeaders): Observable<DriverDto[]> {
    return this.baseApiService.post(this.baseUrl, ids, { auth, params: { include } }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneV2Dto): Observable<DriverDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getDriverSummary(companyId: string, auth: AuthHeaders): Observable<DriverStatusSummaryDto> {
    return this.baseApiService.get(`${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverStatusSummaryDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getGroupedByDriversByDate(
    companyId: string,
    params: DataSourceParams<GetManyV2Dto, { date: string }>,
    auth: AuthHeaders,
  ): Observable<PageableListV2Dto<GroupedByDriversByDateDto>> {
    return this.baseApiService
      .get(
        `${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs/${params.extra.date}/by-date/grouped-by-drivers`,
        {
          auth,
          params: { ...params.params },
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<GroupedByDriversByDateDto[]>) => this.baseApiService.mapResponseV2(response)),
        map((response: ResponseDto<GroupedByDriversByDateDto[]>) => {
          return {
            data: response.data,
            ...response.meta,
          };
        }),
      );
  }

  getGroupedDriverLogsSummaryByDate(
    companyId: string,
    date: string,
    params: GetManyV2Dto,
    auth: AuthHeaders,
  ): Observable<PageableListV2Dto<GroupedByDriversByDateDto>> {
    return this.baseApiService
      .get(`${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs/${date}/by-date/grouped-by-drivers`, {
        auth,
        params: { ...params },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<GroupedByDriversByDateDto[]>) => this.baseApiService.mapResponseV2(response)),
        map((response: ResponseDto<GroupedByDriversByDateDto[]>) => {
          return {
            data: response.data,
            ...response.meta,
          };
        }),
      );
  }

  getActivityReportsByDateRange(
    companyId: string,
    params: DataSourceParams<GetManyV2Dto, { date: string }>,
    auth: AuthHeaders,
  ): Observable<PageableListV2Dto<ActivityReportsDto>> {
    return this.baseApiService
      .get(
        `${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs/distances/${params.extra.date}/by-date-range`,
        {
          auth,
          params: { ...params.params },
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<ActivityReportsDto[]>) => this.baseApiService.mapResponseV2(response)),
        map((response: ResponseDto<ActivityReportsDto[]>) => {
          return {
            ...response,
            ...response.meta,
          };
        }),
      );
  }

  getIndexedDriverLogs(auth: AuthHeaders, params?: GetManyV2Dto): Observable<LogRecord[]> {
    return this.baseApiService.get(`${this.baseUrl}/all/event-logs`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<LogRecord[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getDriverLogs(id: string, auth: AuthHeaders, date: string, original?: boolean): Observable<DriverLogbookRecord> {
    return this.baseApiService
      .get(`${this.baseUrl}/${id}/event-logs/${date}/by-date${original ? '/original' : ''}?withRecordStatuses=3`, {
        auth,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<DriverLogbookRecord>) => this.baseApiService.mapResponse(response)),
      );
  }

  getLogbookDailyInfo(id: string, auth: AuthHeaders, dateRange: string): Observable<DailyInfo[]> {
    return this.baseApiService
      .get(`${this.baseUrl}/${id}/event-logs/${dateRange}/by-date-range/daily-info`, {
        auth,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<DailyInfo[]>) => this.baseApiService.mapResponse(response)),
      );
  }

  getDriverActiveLogs(id: string, auth: AuthHeaders, date: string): Observable<DriverLogbookRecord> {
    return this.baseApiService
      .get(`${this.baseUrl}/${id}/event-logs/${date}/by-date`, {
        auth,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<DriverLogbookRecord>) => this.baseApiService.mapResponse(response)),
      );
  }

  getDriverWeightStationLogs(id: string, auth: AuthHeaders, date: string): Observable<DriverWeightStationLogs> {
    return this.baseApiService
      .get(`${this.baseUrl}/${id}/event-logs/weight-stations/${date}/by-date`, {
        auth,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<DriverWeightStationLogs>) => this.baseApiService.mapResponse(response)),
      );
  }

  getDriverLogsInRange(id: string, auth: AuthHeaders, range: string): Observable<DriverLogbookRecord> {
    return this.baseApiService
      .get(`${this.baseUrl}/${id}/event-logs/${range}/by-date-range`, {
        auth,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<DriverLogbookRecord>) => this.baseApiService.mapResponse(response)),
      );
  }

  transferOutputFile(id: string, data: TransferOutputFileDto, auth: AuthHeaders): Observable<FmcsaReportsDto> {
    return this.baseApiService.post(`${this.baseUrl}/${id}/event-logs/output-files/transfer`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<FmcsaReportsDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  testTransferOutputFile(id: string, data: TransferOutputFileDto, auth: AuthHeaders): Observable<FmcsaValidationDto> {
    return this.baseApiService.post(`${this.baseUrl}/${id}/event-logs/output-files/transfer/test`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<FmcsaValidationDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<DriverDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DriverDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  downloadCSVFile(auth: AuthHeaders, params?: LogbookReportParams): Observable<Blob> {
    return this.baseApiService
      .get<Blob>(`${this.baseUrl}/${params.driverId}/event-logs/output-files/csv`, {
        auth,
        params: { date: params.date },
        responseType: 'blob' as 'json',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: Blob) => response),
      );
  }

  getReportByDateRangePDF(auth: AuthHeaders, params?: LogbookReportParams): Observable<Blob> {
    return this.baseApiService
      .get<Blob>(`${this.baseUrl}/${params.driverId}/event-logs/reports/${params.date}/by-date-range/pdf`, {
        auth,
        responseType: 'blob' as 'json',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: Blob) => response),
      );
  }

  getReportByDateRangeCSV(auth: AuthHeaders, params?: LogbookReportParams): Observable<Blob> {
    return this.baseApiService
      .get<Blob>(`${this.baseUrl}/${params.driverId}/event-logs/output-files/csv`, {
        auth,
        params: { dateRange: params.date },
        responseType: 'blob' as 'json',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: Blob) => response),
      );
  }

  getReportByDateRangeHTML(auth: AuthHeaders, params?: LogbookReportParams): Observable<string> {
    return this.baseApiService
      .get<string>(`${this.baseUrl}/${params.driverId}/event-logs/reports/${params.date}/by-date-range/html`, {
        auth,
        responseType: 'text',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          let parsedError = error;
          if (typeof error.error === 'string') {
            try {
              const parsedErrorBody = JSON.parse(error.error);
              parsedError = {
                ...error,
                error: parsedErrorBody,
              };
            } catch (e) {}
          }
          this.baseApiService.catchApiError(parsedError);
        }),
        map((response: string) => response),
      );
  }

  setCoDriverByDate(
    companyId: string,
    id: string,
    date: string,
    data: { coDriverId: string },
    auth: AuthHeaders,
  ): Observable<EventLogCoDriverPeriod> {
    return this.baseApiService
      .post(
        `${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs/co-driver-periods/${id}/${date}/by-date`,
        data,
        {
          auth,
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<EventLogCoDriverPeriod>) => this.baseApiService.mapResponse(response)),
      );
  }

  listCoDriversByDateRange(
    companyId: string,
    auth: AuthHeaders,
    params?: GetManyV2Dto,
  ): Observable<EventLogCoDriverPeriod[]> {
    return this.baseApiService
      .get(`${this.baseUrlSummary}/${companyId}/drivers/summary/event-logs/co-driver-periods`, { auth, params })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<EventLogCoDriverPeriod[]>) => this.baseApiService.mapResponse(response)),
      );
  }
}
